/**
 * LeftShield Portal - Audit Workspace Page
 * Audit engagement workspace for managing engagements and evidence packaging.
 *
 * Route: /audit-workspace | Nav: Compliance > Audit Workspace
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  tokens,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Textarea,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DocumentTableRegular,
  DismissCircleRegular,
  PersonRegular,
  CalendarRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type { AuditEngagement, EvidenceBundle, CreateEngagementRequest } from '../types';

const getStatusColor = (status: string): 'success' | 'warning' | 'danger' | 'informative' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'closed':
      return 'informative';
    default:
      return 'informative';
  }
};

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

export const AuditWorkspacePage: React.FC = () => {
  const ps = usePageStyles();
  const queryClient = useQueryClient();

  const [selectedEngagementId, setSelectedEngagementId] = useState<number | null>(null);
  const [bundleSuccess, setBundleSuccess] = useState<string | null>(null);

  // Create Engagement dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [engTitle, setEngTitle] = useState('');
  const [engAuditorName, setEngAuditorName] = useState('');
  const [engAuditorEmail, setEngAuditorEmail] = useState('');
  const [engFrameworks, setEngFrameworks] = useState('');
  const [engScopeFrom, setEngScopeFrom] = useState('');
  const [engScopeTo, setEngScopeTo] = useState('');
  const [engNotes, setEngNotes] = useState('');

  const createEngagementMutation = useMutation({
    mutationFn: (payload: CreateEngagementRequest) => auditApi.createEngagement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditWorkspace', 'engagements'] });
      setIsCreateDialogOpen(false);
      setEngTitle('');
      setEngAuditorName('');
      setEngAuditorEmail('');
      setEngFrameworks('');
      setEngScopeFrom('');
      setEngScopeTo('');
      setEngNotes('');
    },
  });

  const handleSubmitEngagement = () => {
    const payload: CreateEngagementRequest = {
      title: engTitle,
      auditorName: engAuditorName,
      auditorEmail: engAuditorEmail,
      frameworksInScope: engFrameworks,
      scopeFrom: engScopeFrom,
      scopeTo: engScopeTo,
      notes: engNotes || undefined,
    };
    createEngagementMutation.mutate(payload);
  };

  const { data: engagements, isLoading } = useQuery<AuditEngagement[]>({
    queryKey: ['auditWorkspace', 'engagements'],
    queryFn: () => auditApi.listEngagements(),
  });

  const { data: bundles, isLoading: bundlesLoading } = useQuery<EvidenceBundle[]>({
    queryKey: ['auditWorkspace', 'bundles', selectedEngagementId],
    queryFn: () => auditApi.listBundles(selectedEngagementId!),
    enabled: selectedEngagementId != null,
  });

  const generateBundleMutation = useMutation({
    mutationFn: (engagementId: number) => auditApi.generateBundle(engagementId),
    onSuccess: (result) => {
      setBundleSuccess(`Bundle generated: ${result.fileName} (${result.recordCount} records)`);
      queryClient.invalidateQueries({ queryKey: ['auditWorkspace', 'bundles'] });
      setTimeout(() => setBundleSuccess(null), 5000);
    },
  });

  const closeEngagementMutation = useMutation({
    mutationFn: (engagementId: number) => auditApi.closeEngagement(engagementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditWorkspace', 'engagements'] });
    },
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading audit engagements..." />
      </div>
    );
  }

  const selectedEngagement = engagements?.find((e) => e.id === selectedEngagementId) ?? null;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Audit Workspace"
        subtitle="Manage audit engagements and evidence packaging"
        actions={
          <Dialog open={isCreateDialogOpen} onOpenChange={(_, data) => setIsCreateDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<AddRegular />}>New Engagement</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogTitle>Create Audit Engagement</DialogTitle>
              <DialogBody>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                    <Field label="Title" required>
                      <Input
                        value={engTitle}
                        onChange={(_, data) => setEngTitle(data.value)}
                        placeholder="e.g., SOC 2 Type II — Q1 2026"
                      />
                    </Field>
                    <Field label="Auditor Name" required>
                      <Input
                        value={engAuditorName}
                        onChange={(_, data) => setEngAuditorName(data.value)}
                        placeholder="e.g., Jane Smith"
                      />
                    </Field>
                    <Field label="Auditor Email" required>
                      <Input
                        type="email"
                        value={engAuditorEmail}
                        onChange={(_, data) => setEngAuditorEmail(data.value)}
                        placeholder="e.g., jane.smith@auditor.com"
                      />
                    </Field>
                    <Field label="Frameworks In Scope" required hint="Comma-separated list">
                      <Input
                        value={engFrameworks}
                        onChange={(_, data) => setEngFrameworks(data.value)}
                        placeholder="e.g., SOC2, ISO27001, NIST-CSF"
                      />
                    </Field>
                    <Field label="Scope From" required>
                      <Input
                        type="date"
                        value={engScopeFrom}
                        onChange={(_, data) => setEngScopeFrom(data.value)}
                      />
                    </Field>
                    <Field label="Scope To" required>
                      <Input
                        type="date"
                        value={engScopeTo}
                        onChange={(_, data) => setEngScopeTo(data.value)}
                      />
                    </Field>
                    <Field label="Notes">
                      <Textarea
                        value={engNotes}
                        onChange={(_, data) => setEngNotes(data.value)}
                        placeholder="Additional notes for this engagement..."
                        rows={3}
                      />
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  <Button
                    appearance="primary"
                    onClick={handleSubmitEngagement}
                    disabled={
                      !engTitle || !engAuditorName || !engAuditorEmail ||
                      !engFrameworks || !engScopeFrom || !engScopeTo ||
                      createEngagementMutation.isPending
                    }
                  >
                    {createEngagementMutation.isPending ? 'Creating...' : 'Create Engagement'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        }
      />

      {/* Success feedback banner */}
      {bundleSuccess && (
        <Card>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacingHorizontalM,
              padding: tokens.spacingVerticalM,
              backgroundColor: tokens.colorPaletteGreenBackground1,
              borderRadius: tokens.borderRadiusMedium,
            }}
          >
            <CheckmarkCircleRegular
              style={{ fontSize: '1.25rem', color: tokens.colorPaletteGreenForeground1 }}
            />
            <Body1 style={{ color: tokens.colorPaletteGreenForeground1 }}>{bundleSuccess}</Body1>
          </div>
        </Card>
      )}

      {/* Engagements List */}
      {!engagements || engagements.length === 0 ? (
        <Card>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
              gap: tokens.spacingVerticalM,
              textAlign: 'center',
            }}
          >
            <DocumentTableRegular
              style={{ fontSize: '3rem', color: tokens.colorNeutralForeground3 }}
            />
            <Body1 style={{ fontWeight: 600 }}>No audit engagements</Body1>
            <Body2 style={{ color: tokens.colorNeutralForeground3, maxWidth: '420px' }}>
              Audit Status: NOT READY. Evidence chain incomplete. No active audit engagement. Risk: Audit will fail if started today. Action: Create an audit engagement to begin.
            </Body2>
          </div>
        </Card>
      ) : (
        <div className={ps.metricGrid}>
          {engagements.map((engagement) => {
            const isActive = engagement.status.toLowerCase() === 'active';
            const isSelected = selectedEngagementId === engagement.id;

            return (
              <Card
                key={engagement.id}
                style={{
                  cursor: 'pointer',
                  outline: isSelected
                    ? `2px solid ${tokens.colorBrandStroke1}`
                    : undefined,
                }}
                onClick={() =>
                  setSelectedEngagementId(isSelected ? null : engagement.id)
                }
              >
                <CardHeader
                  header={<Body1 style={{ fontWeight: 600 }}>{engagement.title}</Body1>}
                  action={
                    <Badge appearance="filled" color={getStatusColor(engagement.status)}>
                      {engagement.status}
                    </Badge>
                  }
                />
                <div className={ps.cardBody}>
                  <div className={ps.rowList}>
                    {/* Auditor */}
                    <div className={ps.rowItem}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: tokens.spacingHorizontalS,
                        }}
                      >
                        <PersonRegular
                          style={{
                            fontSize: '1rem',
                            color: tokens.colorNeutralForeground3,
                          }}
                        />
                        <Body2>{engagement.auditorName}</Body2>
                      </div>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        {engagement.auditorEmail}
                      </Body2>
                    </div>

                    {/* Frameworks */}
                    <div className={ps.rowItem}>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Frameworks</Body2>
                      <div
                        style={{
                          display: 'flex',
                          gap: tokens.spacingHorizontalXS,
                          flexWrap: 'wrap',
                        }}
                      >
                        {engagement.frameworksInScope
                          .split(',')
                          .map((fw) => fw.trim())
                          .filter(Boolean)
                          .map((fw) => (
                            <Badge key={fw} appearance="outline" size="small">
                              {fw}
                            </Badge>
                          ))}
                      </div>
                    </div>

                    {/* Date range */}
                    <div className={ps.rowItem}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: tokens.spacingHorizontalS,
                        }}
                      >
                        <CalendarRegular
                          style={{
                            fontSize: '1rem',
                            color: tokens.colorNeutralForeground3,
                          }}
                        />
                        <Body2>
                          {formatDate(engagement.scopeFrom)} &mdash;{' '}
                          {formatDate(engagement.scopeTo)}
                        </Body2>
                      </div>
                    </div>
                  </div>

                  {/* Actions for active engagements */}
                  {isActive && (
                    <div
                      style={{
                        display: 'flex',
                        gap: tokens.spacingHorizontalM,
                        marginTop: tokens.spacingVerticalS,
                      }}
                    >
                      <Button
                        appearance="primary"
                        icon={<DocumentTableRegular />}
                        disabled={generateBundleMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          generateBundleMutation.mutate(engagement.id);
                        }}
                      >
                        {generateBundleMutation.isPending &&
                        generateBundleMutation.variables === engagement.id
                          ? 'Generating...'
                          : 'Generate Bundle'}
                      </Button>
                      <Button
                        appearance="subtle"
                        icon={<DismissCircleRegular />}
                        disabled={closeEngagementMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          closeEngagementMutation.mutate(engagement.id);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected Engagement Detail — Evidence Bundles */}
      {selectedEngagement && (
        <Card>
          <CardHeader
            header={
              <Title2>Evidence Bundles — {selectedEngagement.title}</Title2>
            }
            description={
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                Generated evidence packages for this engagement
              </Body2>
            }
          />
          <div className={ps.cardBody}>
            {bundlesLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: tokens.spacingVerticalXL,
                }}
              >
                <Spinner size="small" label="Loading bundles..." />
              </div>
            ) : !bundles || bundles.length === 0 ? (
              <Body2 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', padding: tokens.spacingVerticalXL }}>
                No evidence bundles generated yet. Use the &quot;Generate Bundle&quot; button on an
                active engagement to create one.
              </Body2>
            ) : (
              <div className={ps.rowList}>
                {bundles.map((bundle) => (
                  <div key={bundle.id} className={ps.rowItem}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.spacingHorizontalS,
                        flex: 1,
                      }}
                    >
                      <DocumentTableRegular
                        style={{
                          fontSize: '1.25rem',
                          color: tokens.colorBrandForeground1,
                        }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: tokens.spacingVerticalXXS,
                        }}
                      >
                        <Body1 style={{ fontWeight: 500 }}>{bundle.fileName}</Body1>
                        <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                          {bundle.recordCount} records &middot;{' '}
                          {(bundle.sizeBytes / 1024).toFixed(1)} KB &middot;{' '}
                          {formatDate(bundle.generatedAt)}
                        </Body2>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.spacingHorizontalS,
                      }}
                    >
                      <Badge appearance="outline" size="small">
                        {bundle.frameworksIncluded}
                      </Badge>
                      <Badge
                        appearance="filled"
                        color={
                          bundle.status.toLowerCase() === 'complete'
                            ? 'success'
                            : 'informative'
                        }
                        size="small"
                      >
                        {bundle.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
